import React from 'react';
import { Activity, BellRing, CalendarClock, CheckCircle2, FileText, Mail, MessageCircle, Upload, Users } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';
import ClinicalPanelHub from '../components/admin/ClinicalPanelHub';

const DemoCredentialPill = ({ label, value, testId }) => (
  <div className="rounded-[20px] border border-border-subtle bg-background/55 px-4 py-3" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-2 text-sm font-semibold text-text-heading break-all">{value}</p>
  </div>
);

const ValueCard = ({ icon: Icon, title, text, testId }) => (
  <div className="rounded-[24px] border border-border-subtle bg-card/40 p-5" data-testid={testId}>
    <div className="flex items-center gap-3 text-text-heading">
      <div className="flex h-10 w-10 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Icon className="h-5 w-5" /></div>
      <p className="text-base font-semibold">{title}</p>
    </div>
    <p className="mt-4 text-sm text-text-body">{text}</p>
  </div>
);

export default function ClinicDemoDashboardPage() {
  const { authFetch } = useAuth();

  return (
    <>
      <SEO title="Demo Clínica Premium — Neuralia369" description="Demo clínica con CRM, documentos, agenda y comunicaciones listas para enseñar al cliente." />
      <main className="min-h-screen bg-background px-4 py-10 sm:px-6 lg:px-8" data-testid="clinic-demo-dashboard-page">
        <div className="mx-auto max-w-7xl space-y-8">
          <section className="glass-card overflow-hidden p-8" data-testid="clinic-demo-dashboard-hero">
            <div className="grid gap-8 xl:grid-cols-[1.1fr_0.9fr] xl:items-center">
              <div>
                <p className="editorial-kicker">Demo clínica premium</p>
                <h1 className="mt-4 text-4xl font-heading font-bold text-text-heading sm:text-5xl">CRM clínico, documentos, agenda y acciones listas para vender.</h1>
                <p className="mt-5 max-w-3xl text-sm text-text-body sm:text-base" data-testid="clinic-demo-dashboard-hero-copy">
                  Esta vista está separada del admin interno de Neuralia. Es el demo que puedes enseñar a la clínica: captura de leads, seguimiento de pacientes, documentos, recordatorios, promociones y coordinación operativa desde un solo panel.
                </p>
              </div>

              <div className="space-y-4">
                <DemoCredentialPill label="Acceso demo clínica" value="demo.clinica@neuralia369.com" testId="clinic-demo-dashboard-email-pill" />
                <DemoCredentialPill label="Contraseña demo clínica" value="DemoClinica369!" testId="clinic-demo-dashboard-password-pill" />
                <div className="rounded-[24px] border border-primary/20 bg-primary/10 p-5" data-testid="clinic-demo-dashboard-meeting-pill">
                  <p className="text-xs uppercase tracking-[0.18em] text-primary">Qué enseñar en 3 minutos</p>
                  <div className="mt-3 space-y-2 text-sm text-text-body">
                    <div>1. Leads y pacientes entrando al CRM</div>
                    <div>2. Documentos y Excel/CSV dentro del circuito</div>
                    <div>3. Eventos, recordatorios, WhatsApp y email en modo preparación</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="clinic-demo-dashboard-value-grid">
            <ValueCard icon={Users} title="CRM clínico" text="Leads y pacientes en un pipeline claro, priorizado y fácil de explicar." testId="clinic-demo-dashboard-value-crm" />
            <ValueCard icon={FileText} title="Documentos" text="Consentimientos, presupuestos, informes, imágenes y Excel de contactos dentro del mismo flujo." testId="clinic-demo-dashboard-value-documents" />
            <ValueCard icon={CalendarClock} title="Agenda" text="Eventos, valoraciones, cirugías y recordatorios visuales para el equipo." testId="clinic-demo-dashboard-value-agenda" />
            <ValueCard icon={MessageCircle} title="Comunicación" text="WhatsApp y email preparados para activar cuando cerréis la venta." testId="clinic-demo-dashboard-value-communications" />
          </section>

          <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4" data-testid="clinic-demo-dashboard-highlight-grid">
            <ValueCard icon={Mail} title="Promociones" text="Campañas y recordatorios listos para enseñar, sin depender todavía de credenciales finales." testId="clinic-demo-dashboard-highlight-promotions" />
            <ValueCard icon={Upload} title="Imports" text="Subida de CSV/XLS/XLSX para mostrar cómo incorporan su base real al CRM." testId="clinic-demo-dashboard-highlight-imports" />
            <ValueCard icon={BellRing} title="Recordatorios" text="Plantillas preparadas para seguimiento, confirmación y reactivación de pacientes." testId="clinic-demo-dashboard-highlight-reminders" />
            <ValueCard icon={CheckCircle2} title="Listo para reunión" text="Todo queda organizado como demo externa, sin tocar el admin interno de Neuralia." testId="clinic-demo-dashboard-highlight-ready" />
          </section>

          <section className="glass-card p-4 sm:p-6" data-testid="clinic-demo-dashboard-panel-wrapper">
            <div className="mb-5 flex items-center gap-3 text-text-heading">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary"><Activity className="h-5 w-5" /></div>
              <div>
                <p className="text-xs uppercase tracking-[0.18em] text-text-muted">Demo visual</p>
                <h2 className="text-xl font-semibold">Panel clínico listo para enseñar al cliente</h2>
              </div>
            </div>
            <ClinicalPanelHub authFetch={authFetch} />
          </section>
        </div>
      </main>
    </>
  );
}